import React, { useCallback } from 'react';
import { GenericCard } from 'components/display/Card/Card';
import { NsKey } from 'enums/NsKey';
import { TitleIconFactory } from 'factories/TitleIconFactory';
import { TitleIconKey } from 'enums/titleIconKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import ConfettiSuccess from 'components/display/ConfettiSuccess';
import { contractsAPI } from 'services/swagger-api';
import {
  useQuoteContractualizationData,
  useIsSendingContract,
  useIsFetchingDocuments,
  useIsContractCompleted,
  useEmailConfig
} from '../../store';
import { useSubscriptionProcessActions } from 'features/subscription-process/contexts/SubscriptionProcessContext';
import { useDocumentsSync } from '../../hooks/useDocumentsSync';
import { useSortedDocuments } from '../../hooks/useSortedDocuments';
import { DocumentsList } from './DocumentsList';
import { DocumentCompletionBanner } from './DocumentCompletionBanner';

interface DocumentsCardProps {
  className?: string;
}

export const DocumentsCard: React.FC<DocumentsCardProps> = ({ className }) => {
  const quoteData = useQuoteContractualizationData();
  const isSendingContract = useIsSendingContract();
  const isFetchingDocuments = useIsFetchingDocuments();
  const { refreshSubscriptionProcess } = useSubscriptionProcessActions();
  const { t } = useDynamicTranslation([
    NsKey.Contractualization,
    NsKey.Document
  ]);

  // Hooks centralises
  useDocumentsSync();

  const sortedDocuments = useSortedDocuments();

  // Selecteurs centralises
  const isCompleted = useIsContractCompleted();
  const emailConfig = useEmailConfig();

  const patchAnimationCompleted = useCallback(async () => {
    if (quoteData?.riskId)
      await contractsAPI.patchContractAnimation({ riskId: quoteData.riskId });
    refreshSubscriptionProcess();
  }, [quoteData?.riskId, refreshSubscriptionProcess]);

  return (
    <GenericCard className={className + ' overflow-hidden'}>
      <GenericCard.Header
        icon={
          <TitleIconFactory
            titleIconKey={TitleIconKey.Documents}
            addClassName="-mb-1"
          />
        }
      >
        {t('CONTRACTUALIZATION.TITLE.DOCUMENT_MANAGE')}
      </GenericCard.Header>
      <GenericCard.Body className="relative">
        <DocumentsList
          documents={sortedDocuments}
          isFetchingDocuments={isFetchingDocuments}
          isSendingContract={isSendingContract}
          emailConfig={emailConfig}
        />
        {isCompleted && !quoteData?.isAnimationCompleted && (
          <ConfettiSuccess
            count={100}
            duration={4000}
            onComplete={patchAnimationCompleted}
          />
        )}
        {isCompleted && <DocumentCompletionBanner />}
      </GenericCard.Body>
    </GenericCard>
  );
};

// Backward compatibility export
export { DocumentsCard as DocumentsCardContainer };
